import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatOrderMoney } from "../lib/api";
import Navbar from "../components/Navbar";
import { Package } from "lucide-react";

const STATUS_SK = {
  feldolgozas_alatt: "Spracováva sa",
  reszben_megjott: "Čiastočne prijaté",
  teljesitve: "Splnené",
  // Legacy fallbacks for any orders not yet migrated
  feldolgozva: "Čiastočne prijaté",
  atveheto: "Splnené",
  atveve: "Spracováva sa",
  visszaigazolva: "Čiastočne prijaté",
  szallitas_alatt: "Čiastočne prijaté",
  kiszamlazva: "Splnené",
  feldolgozatlan: "Spracováva sa",
  kesz: "Čiastočne prijaté",
  specialis: "Spracováva sa",
};

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/orders").then((r) => setOrders(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <Navbar />

      <div className="breadcrumb">
        <div className="max-w-[1200px] mx-auto px-4">
          <Link to="/catalog">Domov</Link> / <span style={{ color: "var(--text)" }}>Moje objednávky</span>
        </div>
      </div>

      <main className="max-w-[1200px] mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold mb-2 uppercase tracking-wide">Moje objednávky</h1>
        <div className="border-b-2 mb-5" style={{ borderColor: "var(--brand)" }} />

        {loading ? (
          <div className="card-elev p-8 text-center text-sm" style={{ color: "var(--text-mute)" }}>Načítavanie...</div>
        ) : orders.length === 0 ? (
          <div className="card-elev p-12 text-center" data-testid="no-orders">
            <Package size={42} className="mx-auto mb-3" style={{ color: "var(--text-disabled)" }} />
            <h3 className="font-bold text-lg mb-1">Zatiaľ nemáte žiadne objednávky</h3>
            <p className="text-sm" style={{ color: "var(--text-mute)" }}>Vaše objednávky sa tu zobrazia.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="orders-list">
            {orders.map((o) => (
              <div key={o.id} className="card-elev overflow-hidden" data-testid={`order-${o.id}`}>
                <div className="section-title flex-wrap justify-between">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span>Objednávka #{o.id.slice(0, 8).toUpperCase()}</span>
                    {o.po_number && <span className="text-[11px] opacity-90">VIN: {o.po_number}</span>}
                  </div>
                  <span
                    className="text-[11px] uppercase px-2 py-0.5 rounded"
                    style={{ background: "#fff", color: "var(--brand)" }}
                  >
                    {STATUS_SK[o.status] || o.status}
                  </span>
                </div>

                <div className="p-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs bg-white border-b" style={{ borderColor: "var(--border)" }}>
                  <Stat label="Zadané" value={new Date(o.created_at).toLocaleString("sk-SK")} />
                  <Stat label="Položky" value={o.items.length + " ks"} />
                  <Stat label="Množstvo" value={o.items.reduce((s, i) => s + i.quantity, 0) + " ks"} />
                  <Stat label="Spolu (netto)" value={formatOrderMoney(o.total_display ?? o.total)} accent />
                </div>

                {o.modification_note && (
                  <div className="px-3 py-2 text-xs bg-white border-b" style={{ borderColor: "var(--border)" }} data-testid={`order-mod-note-${o.id}`}>
                    <div className="p-2" style={{ background: "#E8F0FA", border: "1px solid #B9D2EA", borderRadius: 3 }}>
                      <strong style={{ color: "var(--brand)" }}>Objednávka bola upravená:</strong> {o.modification_note}
                    </div>
                  </div>
                )}

                <div className="overflow-x-auto bg-white">
                  <table className="table-b2b">
                    <thead>
                      <tr>
                        <th>Kód</th>
                        <th>Názov</th>
                        {o.items.some((it) => it.arrived) && <th style={{ width: 130 }}>Stav</th>}
                        <th style={{ width: 80 }}>Ks</th>
                        <th style={{ width: 110 }} className="text-right">Netto</th>
                        <th style={{ width: 130 }} className="text-right">Spolu</th>
                      </tr>
                    </thead>
                    <tbody>
                      {o.items.map((it, idx) => (
                        <tr key={idx}>
                          <td><span className="font-mono text-xs" style={{ color: "var(--brand)" }}>{it.sku}</span></td>
                          <td className="font-bold">{it.name}</td>
                          {o.items.some((x) => x.arrived) && (
                            <td data-testid={`item-arrival-${o.id}-${idx}`}>
                              {it.arrived ? (
                                <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded" style={{ background: "#E8F8E8", color: "#0A8043", border: "1px solid #0A8043" }}>
                                  ✔ Prijaté
                                </span>
                              ) : (
                                <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded" style={{ background: "#FFF4E0", color: "#C58800", border: "1px solid #C58800" }}>
                                  ⏳ V objednávaní
                                </span>
                              )}
                            </td>
                          )}
                          <td className="font-mono">{it.quantity}</td>
                          <td className="text-right font-mono">{formatOrderMoney(it.unit_price_display ?? it.unit_price)}</td>
                          <td className="text-right font-mono font-bold">{formatOrderMoney(it.line_total_display ?? it.line_total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div>
      <div className="uppercase text-[10px] font-bold mb-0.5" style={{ color: "var(--text-mute)" }}>{label}</div>
      <div className={`font-mono ${accent ? "text-base font-bold" : "text-sm"}`} style={accent ? { color: "var(--brand)" } : {}}>
        {value}
      </div>
    </div>
  );
}
